import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Browser from '@wetransfer/concorde-browser';

import Actions from '@app/action';
import API from '@app/api';
import Helper from '@app/helper';
import Store from '@app/store';
import Config from '@app/config';

import Analytics from '@module/analytics';
import I18n from '@module/i18n';
import Router from '@module/router';
import LaunchDarkly from '@module/launchdarkly';

import Button from '@component/form/element/button';
import Checkbox from '@component/form/element/checkbox';
import CountryField from '@component/form/element/countryfield';
import Form, { validationIndentifiers } from '@component/form/';
import FormNotification from '@component/form/element/formnotification';
import PanelLoading from '@component/panel/loading';
import PlanSelector from '@component/form/element/planselector';
import TextField from '@component/form/element/textfield';
import VatField from '@component/form/element/vatfield';
import Tiers from '@component/form/tiers';

import scriptLoader from '@helper/script-loader';

import {
  EXPERIMENT_USA_UNLIMITED_OFFER,
  EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_CONTROL,
  EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_PLUS,
} from '@constant/experiments';

export default class SignupForm extends Form {
  static propTypes = {
    route: PropTypes.object,
    user: PropTypes.object,
  };

  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.state = {
      data: {
        billing: 'yearly',
        full_name: '',
        email: '',
        password: '',
        password_confirm: '',
        company_name: '',
        street_and_number: '',
        city: '',
        zipcode: '',
        country_code: Store.getState().session.region.country,
        vat_number: '',
        terms_of_service: false,
        coupon_code: '',
        convert: '',
        sig: '',
      },
      tier: '',
      country: Store.getState().session.region.country,
    };

    this.routeBackElement = null;
    this.formName = 'signup';
    this.createAccount = this.createAccount.bind(this);
    this.getErrorMessage = this.getErrorMessage.bind(this);
    this.getPricing = this.getPricing.bind(this);
    this.validate = this.validate.bind(this);
    this.validateState = this.validateState.bind(this);
    this.handleRouteBackClick = this.handleRouteBackClick.bind(this);
    this.handleTierSelect = this.handleTierSelect.bind(this);
  }

  componentDidMount() {
    const { route, user, account } = Store.getState();
    window.addEventListener('blur', this.validateState, true);
    this.getPricing();

    // TERNOT, update convert and sig in data state.
    const { convert, sig } = route.query;
    if (convert && sig) {
      if (user.loggedIn && !user.storageFull && account.active_subscription) {
        this.assignTransferToUser(convert, sig);
      } else {
        this.assignTransferToSignup(convert, sig);
      }
    }

    this.loadStripe();
  }

  async loadStripe() {
    await scriptLoader(Config.stripe.url);
  }

  async assignTransferToSignup(convert, sig) {
    Store.dispatch(
      Actions.Errors.validateForm({ convert, sig }, 'assign_transfer')
    );
    await this.updateValue('convert', convert);
    this.updateValue('sig', sig);
  }

  async assignTransferToUser(convert, sig) {
    const response = await API.User.assignTransfer({ convert, sig });
    if (response.status === 200) {
      Router.navigateTo('/transfers/' + convert);
    } else {
      Router.navigateTo('/transfers');
      // Show the error after the panel animation is complete
      setTimeout(
        () =>
          Store.dispatch(
            Actions.Notification.setNotification(
              'error',
              I18n.t('transfer.expired.text')
            )
          ),
        1000
      );
    }
  }

  componentWillUnmount() {
    window.removeEventListener('blur', this.validateState, true);
    this.routeBackElement &&
      this.routeBackElement.removeEventListener(
        'click',
        this.handleRouteBackClick
      );
    Store.dispatch(Actions.UI.resetSubscriptionPricing());
  }

  async getPricing() {
    const { route, session } = Store.getState();
    const coupon = route.query.coupon || '';

    if ('coupon' in route.query) {
      this.updateValue('billing', 'yearly', true);
      await this.updateValue('coupon_code', coupon, true);
      this.validateState();
    }

    Store.dispatch(
      Actions.UI.getSubscriptionPricing({
        country_code:
          this.state.data.country_code || session.region.country || '',
        vat_number: '',
        coupon_code: coupon,
        plan_tier: this.state.tier,
      })
    );
  }

  // Returns the first error that comes up for a field. Remote errors have priority over local errors.
  getError(name) {
    const { pending } = Store.getState();

    // Prevent errors when signup is pending (Because the email can suddenly be claimed.)
    if (pending.signup) {
      return false;
    }

    // Remote errors
    if (
      this.getRemoteError(name) &&
      (this.state.data[name] || name === 'country_code')
    ) {
      return this.getRemoteError(name);
    }

    // Local errors
    if (!document.activeElement) {
      return false;
    }
    if (
      document.activeElement.name === name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return true;
    }
    if (
      document.activeElement.name !== name &&
      this.getLocalError(name) &&
      this.state.data[name]
    ) {
      return this.getLocalError(name);
    }

    return false;
  }

  validate(key) {
    const { errors } = this.props;

    // If no value and no remote errors, ignore valid state. (Basically all the 'cannot be blank'-errors)
    if (!this.state.data[key] && errors[this.form] && !errors[this.form][key]) {
      return validationIndentifiers.DONT_CHECK;
    }
    if (this.getError(key)) {
      return validationIndentifiers.NOT_VALID;
    }

    return validationIndentifiers.VALID;
  }

  // Returns an error when the field doesn't pass remote validation
  getRemoteError(name) {
    const { errors } = this.props;
    if (errors[this.formName] && errors[this.formName][name]) {
      const error = errors[this.formName][name][0];

      const errorKey = 'form.account.error.' + name + '.' + error;
      return I18n.t(errorKey);
    }

    return false;
  }

  // Checks for local and remote errors and whether a submit is still pending, is used to (de-)activate submitButton.
  formValidation() {
    const { pending } = this.props;

    if (pending[this.formName]) {
      return false;
    }

    for (const key in this.state.data) {
      if (
        [
          'vat_number',
          'company_name',
          'billing',
          'coupon_code',
          'convert',
          'sig',
        ].indexOf(key) > -1
      ) {
        continue;
      }
      if (!this.state.data[key]) {
        return false;
      }
      if (this.validate(key) === validationIndentifiers.NOT_VALID) {
        return false;
      }
    }

    return true;
  }

  // Dispatches the request to create an account.
  async createAccount(e) {
    e.preventDefault();
    if (!this.formValidation()) {
      return this;
    }

    this.track();

    const { session } = Store.getState();
    Store.dispatch(Actions.Route.setFormState(false));
    await Store.dispatch(
      Actions.User.signup(this.state.data, {
        trk: this.props.route.query.trk || '',
        trk_extra: this.props.route.query.trk_extra,
        wt_sent: Helper.getWTSent(),
        wt_from: Helper.getLastUsedSender(),
        page_version: 30,
        language: session.language,
        plan_tier: this.state.tier,
      })
    );

    if (this.props.user.errors && this.props.user.errors[this.formName]) {
      this.scrollToError();
    }
  }

  track() {
    Analytics.pushDataLayerEvent({
      category: 'Plus',
      action: 'Signup Form Submit',
      label: this.state.data.billing,
      channel: this.props.route.query.trk,
    });
  }

  makePricingBlock() {
    const {
      ui: { subscriptionPricing },
    } = Store.getState();

    const currencyCode = subscriptionPricing.month.currency.toUpperCase();
    const currencySign = I18n.t(`number.currency.${currencyCode}`);

    const yearly = {
      label: I18n.t('form.plan.year.label'),
      price: subscriptionPricing.year.total,
      copy: I18n.t('form.plan.year.copy'),
      subCopy: ` - ${I18n.t('form.plan.year.subcopy')}`,
      defaultValue: 'yearly',
      name: 'yearlyBilling',
      onChange: (e) => this.updateValue('billing', e.target.value),
      checked: this.state.data.billing === 'yearly',
      currencyCode,
      currencySign,
    };

    const monthly = {
      price: subscriptionPricing.month.total,
      label: I18n.t('form.plan.month.label', { currencySign }),
      copy: I18n.t('form.plan.month.copy', { currencySign }),
      subCopy: null,
      defaultValue: 'monthly',
      name: 'monthlyBilling',
      onChange: (e) => this.updateValue('billing', e.target.value),
      disabled: !!this.state.data.coupon_code,
      checked: this.state.data.billing === 'monthly',
      currencyCode,
      currencySign,
    };

    return (
      <React.Fragment>
        <PlanSelector key="planSelector_yearly" {...yearly} />
        <PlanSelector key="planSelector_monthly" {...monthly} />
      </React.Fragment>
    );
  }

  makePricingBlockNewPlusPage() {
    const {
      ui: { subscriptionPricing },
    } = Store.getState();
    const priceYearly = subscriptionPricing.year.total / 12;
    const newPlusPageSuffix = '-new';
    const currency = I18n.t(
      `number.currency.${subscriptionPricing.month.currency.toUpperCase()}`
    );
    const yearly = {
      label: I18n.t(`form.plan.year.label${newPlusPageSuffix}`),
      price: `${currency}${priceYearly}`,
      copy: I18n.t(`form.plan.year.copy${newPlusPageSuffix}`),
      subCopy: I18n.t('form.plan.year.subcopy-new'),
      defaultValue: 'yearly',
      name: 'yearlyBilling',
      onChange: (e) => this.updateValue('billing', e.target.value),
      checked: this.state.data.billing === 'yearly',
      classNames: ['planselector-new'],
    };

    const monthly = {
      price: `${currency}${subscriptionPricing.month.total}`,
      label: I18n.t(`form.plan.month.label${newPlusPageSuffix}`, { currency }),
      copy: I18n.t(`form.plan.month.copy${newPlusPageSuffix}`, { currency }),
      subCopy: I18n.t('form.plan.month.subcopy-new'),
      defaultValue: 'monthly',
      name: 'monthlyBilling',
      onChange: (e) => this.updateValue('billing', e.target.value),
      disabled: !!this.state.data.coupon_code,
      checked: this.state.data.billing === 'monthly',
      classNames: ['planselector-new'],
    };

    return (
      <React.Fragment>
        <PlanSelector key="planSelector_yearly" {...yearly} />
        <PlanSelector key="planSelector_monthly" {...monthly} />
      </React.Fragment>
    );
  }

  handleRouteBackClick() {
    // We don't want to display the action bar that ask users to stay or leave after form changes
    // when we navigate back to tiers.
    Store.dispatch(Actions.Route.setFormState(false));
    this.setState({ tier: '' });
    // Hide the route back link
    this.props.setReturnPath('', '');
  }

  handleTierSelect(event) {
    const tier = event.target.dataset.tier;
    const { route, setReturnPath } = this.props;
    setReturnPath(`/plus?trk=${route.query.trk}`, 'Plans');
    this.setState({ tier });
  }

  getCountryFieldVariant() {
    // Lock down the country for specific country users as long as the following experiments are live:
    // * https://github.com/WeTransfer/frontend/issues/5622 (CA)
    // * https://github.com/WeTransfer/growth/issues/260 (US)
    // * https://github.com/WeTransfer/growth/issues/294 (IN)

    const { country } = this.state;

    return country === 'CA' || country === 'US' || country === 'IN'
      ? 'readonly'
      : 'default';
  }

  render() {
    const {
      ui: { subscriptionPricing },
    } = Store.getState();

    if (!subscriptionPricing) {
      return <PanelLoading />;
    }

    const countryFromState = this.state.country;
    const userHasSelectedTier = this.state.tier !== '';
    const USAUnlimitedExperimentUnlimitedPlus =
      !Browser.isMobile &&
      countryFromState === 'US' &&
      LaunchDarkly.getVariant(
        EXPERIMENT_USA_UNLIMITED_OFFER,
        EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_CONTROL
      ) === EXPERIMENT_USA_UNLIMITED_OFFER_VARIANT_UNLIMITED_PLUS;

    if (USAUnlimitedExperimentUnlimitedPlus && !userHasSelectedTier) {
      return <Tiers onTierSelect={this.handleTierSelect} />;
    }

    if (USAUnlimitedExperimentUnlimitedPlus && userHasSelectedTier) {
      this.routeBackElement = document.getElementsByClassName('panel__back')[0];
      this.routeBackElement &&
        this.routeBackElement.addEventListener(
          'click',
          this.handleRouteBackClick
        );
    }

    const { pending, errors, route, isNewPlusPage } = this.props;
    const active = this.formValidation();

    let submitText = I18n.t('form.account.complete_order');
    if (pending[this.formName]) {
      submitText = I18n.t('form.submitting');
    }

    // The convert code-error is an odd one. Since it is stored in a different property in our Error-store and is shouldn't prevent the user from submitting
    const convertError = errors.assign_transfer
      ? [
          <FormNotification
            key="account_error_converter"
            message={I18n.t(
              `form.account.error.convert.${errors.assign_transfer.convert[0]}`
            )}
            type="error"
          />,
          <br key="account_error_break" />,
        ]
      : null;

    let formClassName;
    let title;
    let plans;

    // Items that need to be adjusted when we show the new Plus page
    if (isNewPlusPage) {
      formClassName = 'signup-form signup-form-new';
      title = (
        <h2 className="signup-form-new__title">{I18n.t('form.plan.title')}</h2>
      );
      plans = this.makePricingBlockNewPlusPage();
    } else {
      const titleClassName = classnames('signup-form__title', {
        'signup-form__title--with-tiers':
          USAUnlimitedExperimentUnlimitedPlus && userHasSelectedTier,
      });
      title = (
        <h2 className={titleClassName}>
          {I18n.t('form.account.signup_for_plus')}
        </h2>
      );
      plans = this.makePricingBlock();
    }

    const wrapperClassName = classnames({
      signup: isNewPlusPage,
      'signup-form': !isNewPlusPage,
    });

    return (
      <div className={wrapperClassName}>
        {title}
        {USAUnlimitedExperimentUnlimitedPlus && (
          <p className="signup-form__subtitle">
            {this.state.tier === 'plus' && 'Send up to 20GB, 100GB of storage'}
            {this.state.tier === 'unlimited' &&
              'Send files of any size, 500GB of storage'}
          </p>
        )}
        <form
          method="post"
          onSubmit={this.createAccount}
          className={formClassName}
        >
          {convertError}
          {subscriptionPricing.year.discount_rate === 100 ? (
            <legend className="signup-form__legend">
              {I18n.t('form.account.legend.free_plus')}
            </legend>
          ) : (
            plans
          )}
          <TextField
            autoFocus
            label={I18n.t('form.account.label.full_name')}
            value={this.state.data.full_name}
            name="full_name"
            onChange={(e) => this.updateValue('full_name', e.target.value)}
            valid={0}
          />
          <FormNotification
            message={this.getErrorMessage('full_name')}
            type="error"
          />
          <TextField
            type="email"
            label={I18n.t('form.account.label.email')}
            value={this.state.data.email}
            name="email"
            onChange={(e) => this.updateValue('email', e.target.value)}
            valid={this.validate('email')}
          />
          <FormNotification
            message={this.getErrorMessage('email')}
            type="error"
          />
          <TextField
            value={this.state.data.password}
            label={I18n.t('form.account.label.password')}
            name="password"
            type="password"
            onChange={(e) => this.updateValue('password', e.target.value)}
            valid={this.validate('password')}
            description={I18n.t('form.account.description.password')}
          />
          <FormNotification
            message={this.getErrorMessage('password')}
            type="error"
          />
          <TextField
            value={this.state.data.password_confirm}
            label={I18n.t('form.account.label.password_confirm')}
            name="password_confirm"
            type="password"
            onChange={(e) =>
              this.updateValue('password_confirm', e.target.value)
            }
            valid={this.validate('password_confirm')}
          />
          <FormNotification
            message={this.getErrorMessage('password_confirm')}
            type="error"
          />
          <legend className="signup-form__legend">
            {I18n.t('form.account.legend.details')}
          </legend>
          <TextField
            label={
              I18n.t('form.account.label.company') +
              ' ' +
              I18n.t('form.account.label.not_required')
            }
            value={this.state.data.company_name}
            name="company_name"
            onChange={(e) => this.updateValue('company_name', e.target.value)}
          />
          <FormNotification
            message={this.getErrorMessage('company_name')}
            type="error"
          />
          <VatField
            value={this.state.data.vat_number}
            errorMessage={this.getErrorMessage('vat_number')}
            updateValue={this.updateValue}
            resetError={this.resetError}
            formName={this.formName}
            countryCode={this.state.data.country_code}
          />
          <TextField
            label={I18n.t('form.account.label.street_and_number')}
            value={this.state.data.street_and_number}
            name="street_and_number"
            onChange={(e) =>
              this.updateValue('street_and_number', e.target.value)
            }
            valid={0}
          />
          <FormNotification
            message={this.getErrorMessage('street_and_number')}
            type="error"
          />
          <TextField
            label={I18n.t('form.account.label.city')}
            value={this.state.data.city}
            name="city"
            onChange={(e) => this.updateValue('city', e.target.value)}
            valid={0}
          />
          <FormNotification
            message={this.getErrorMessage('city')}
            type="error"
          />
          <TextField
            label={I18n.t('form.account.label.zipcode')}
            value={this.state.data.zipcode}
            name="zipcode"
            onChange={(e) => this.updateValue('zipcode', e.target.value)}
            valid={0}
          />
          <FormNotification
            message={this.getErrorMessage('zipcode')}
            type="error"
          />
          <CountryField
            value={this.state.data.country_code}
            defaultValue={countryFromState}
            errorMessage={this.getErrorMessage('country_code')}
            updateValue={this.updateValue}
            onUpdate={this.getPricing}
            variant={this.getCountryFieldVariant()}
          />
          {'coupon' in route.query &&
          !subscriptionPricing.year.discount_rate ? (
            <div className="signup-form__coupon">
              <legend className="signup-form__legend">
                {I18n.t('form.account.legend.coupon_code')}
              </legend>
              <TextField
                label={I18n.t('form.account.label.coupon_code')}
                value={this.state.data.coupon_code}
                name="coupon_code"
                onChange={(e) =>
                  this.updateValue('coupon_code', e.target.value)
                }
                valid={this.validate('coupon_code')}
              />
              <FormNotification
                message={this.getErrorMessage('coupon_code')}
                type="error"
              />
            </div>
          ) : null}
          <Checkbox
            label={I18n.t('form.account.terms_conditions', {
              options: 'tabindex="-1" data-route-context="plus"',
            })}
            name="terms_of_service"
            onChange={(e) =>
              this.updateValue('terms_of_service', e.target.checked)
            }
            checked={this.state.data.terms_of_service}
          />{' '}
          <br />
          <FormNotification
            message={this.getErrorMessage('terms_of_service')}
            type="error"
          />
          <Button
            onClick={this.createAccount}
            active={active}
            text={submitText}
            submit={true}
          />
        </form>
      </div>
    );
  }
}
